/* eslint-disable no-underscore-dangle */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useContext, useEffect, useRef, useMemo } from 'react';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { Context as AlertContext } from 'contexts/alert';
import { getFullDateInCustomTimezone } from 'utils/dateManagement';
import { PageTitle } from 'components/layouts/pageTitle';
import { DataColumn, List, ListHeader } from 'components/lists';
import { DropDownList } from 'components/formInputs/dropDownList';
import { PageContent } from 'components/layouts/pageContent';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { FilterBlock, FilterField } from 'components/layouts/filterBlock';
import { ListLayout } from 'components/layouts/listLayout';
import { NoRestaurantsBlock } from 'components/blocks/noRestaurants';
import { roundDecimal } from 'utils/roundingHelpers';
import { distributeUnassignedTips, getTipsForAssignment } from 'api/payment';
import { FormButton } from 'components/buttons/formButton';
import { TextboxWrapper } from 'components/formInputs/wrappers/textboxWrapper';

export const AssignTips = () => {
  const { state: auth } = useContext(AuthContext);
  const {
    state: companyState,
    getCompanyRestaurants,
    shouldDisplayNoRestaurantBlock,
  } = useContext(CompanyContext);
  const { alertController } = useContext(AlertContext);

  const [isLoadingUnassigned, setIsLoadingUnassigned] = useState(false);
  const [isCreatingAssignment, setIsCreatingAssignment] = useState(false);

  const [filterRestaurant, setFilterRestaurant, filterRestaurantRef] = useLocalStorage(
    `assign-tips-filter-restaurant-${auth.company?.company}`,
    '',
  );

  const currentFilteredData = useRef({
    restaurantId: '',
    restaurantName: '',
    timezone: '',
    currency: '',
  }).current;

  const [tipsAssignData, setTipsAssignData] = useState(null);

  // employeeId: { value }
  const [assignments, setAssignments] = useState({});

  useEffect(() => {
    getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company });
  }, []);

  const { totalAssignedTips, totalLeftToAssign } = useMemo(() => {
    let result = 0;

    if (assignments) {
      Object.values(assignments).forEach((assignment) => {
        result += parseFloat(assignment.value) || 0;
      });
    }

    const totalAssignedTemp = roundDecimal(result);
    let totalLeftToAssignTemp = tipsAssignData
      ? roundDecimal((tipsAssignData.totalUndistributedTips || 0) - totalAssignedTemp)
      : 0;

    if (totalLeftToAssignTemp < 0) {
      totalLeftToAssignTemp = 0;
    }

    return { totalAssignedTips: totalAssignedTemp, totalLeftToAssign: totalLeftToAssignTemp };
  }, [assignments, tipsAssignData]);

  const onGetUnassigned = async () => {
    setIsLoadingUnassigned(true);

    const response = await getTipsForAssignment({
      companyId: auth.company?.company,
      token: auth.token,
      restaurantId: filterRestaurantRef.value,
      alertController,
    });

    const restaurantData = companyState.restaurants?.find(
      (x) => x._id === filterRestaurantRef.value,
    );

    currentFilteredData.restaurantId = filterRestaurantRef.value;
    currentFilteredData.restaurantName = restaurantData?.name;
    currentFilteredData.timezone = restaurantData?.zone;
    currentFilteredData.currency = restaurantData?.currency || 'EUR';

    if (response && response.employees && response.employees.length > 0) {
      const formedAssignments = {};

      const equallyDistributedAmount = roundDecimal(
        response.totalUndistributedTips / response.employees.length,
      );

      let totalAssigned = 0;

      response.employees.forEach((employee) => {
        const leftToAssign = roundDecimal(response.totalUndistributedTips - totalAssigned);

        let amountToAssign = equallyDistributedAmount;

        if (leftToAssign < equallyDistributedAmount) {
          amountToAssign = leftToAssign;
        }

        formedAssignments[employee._id] = { value: amountToAssign, isManual: false };

        totalAssigned = roundDecimal(totalAssigned + amountToAssign);
      });

      setAssignments(formedAssignments);
    } else {
      setAssignments({});
    }

    setIsLoadingUnassigned(false);
    setTipsAssignData(response);
  };

  const onDistributeTips = async () => {
    setIsCreatingAssignment(true);

    const formedDistributions = [];

    Object.entries(assignments).forEach(([employeeId, value]) => {
      formedDistributions.push({
        employeeId,
        amount: roundDecimal(parseFloat(value.value) || 0),
      });
    });

    const response = await distributeUnassignedTips({
      token: auth.token,
      companyId: auth.company?.company,
      alertController,
      restaurantId: currentFilteredData.restaurantId,
      fromDate: tipsAssignData.fromDate,
      toDate: tipsAssignData.toDate,
      distributions: formedDistributions,
    });

    if (response && response.assignmentId) {
      onGetUnassigned();
    }

    setIsCreatingAssignment(false);
  };

  const renderRangeSlider = (id, value) => {
    const maxAllowed = roundDecimal((parseFloat(value) || 0) + (totalLeftToAssign || 0));

    return (
      <div className="flex flex-row items-center">
        <input
          id={`assigne-range-${id}`}
          type="range"
          value={parseFloat(value)}
          className="w-full max-w-xs h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          onChange={(e) => {
            setAssignments((prev) => {
              const newVal = { ...prev };

              newVal[id].value = parseFloat(e.target.value) || 0;
              newVal[id].isManual = true;

              return newVal;
            });
          }}
          min="0"
          max={maxAllowed}
          step="0.01"
        />
        <div className="pl-3">
          <div className="text-left whitespace-nowrap text-sm text-gray-900 w-24">
            <TextboxWrapper
              value={value}
              onChange={(e) => {
                setAssignments((prev) => {
                  const newVal = { ...prev };

                  const adjustedNewValue =
                    (parseFloat(e.target.value) || 0) > maxAllowed ? maxAllowed : e.target.value;

                  newVal[id].value = adjustedNewValue;
                  newVal[id].isManual = true;

                  return newVal;
                });
              }}
            />
          </div>
          <div className="text-left whitespace-nowrap text-xs text-gray-500 w-24 mt-1">
            (
            {roundDecimal(
              ((parseFloat(value) || 0) / (tipsAssignData?.totalUndistributedTips || 1)) * 100,
            )}
            %)
          </div>
        </div>
      </div>
    );
  };

  if (shouldDisplayNoRestaurantBlock()) {
    return <NoRestaurantsBlock />;
  }

  return (
    <ListLayout isLoading={companyState.isLoadingRestaurants}>
      <PageTitle title="Assign Tips" isTopLevel />

      <PageContent>
        <FilterBlock
          buttons={[
            {
              onPress: onGetUnassigned,
              text: 'Search',
              class:
                'ml-3 bg-indigo-600 border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center items-center text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50',
              disabled: !!isLoadingUnassigned,
              isLoading: !!isLoadingUnassigned,
            },
          ]}
        >
          <FilterField>
            <DropDownList
              showLabel
              id="restaurants"
              name="restaurants"
              label="Restaurant"
              value={filterRestaurant}
              onChange={(e) => {
                setFilterRestaurant(e.target.value);
              }}
              options={[
                { label: '-', value: '' },
                ...(companyState.restaurants || []).map((restaurant) => ({
                  label: restaurant.name,
                  value: restaurant._id,
                })),
              ]}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            />
          </FilterField>
        </FilterBlock>
        {!!tipsAssignData && !isLoadingUnassigned && (
          <>
            <ListHeader
              title="Tips Distribution"
              description="Distribute unassigned tips between employees"
            />

            <div className="my-10 max-w-8xl mx-auto">
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 sm:px-6 py-5 grid grid-cols-3 gap-4">
                  <div className="col-span-3">
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">
                      Restaurant: {currentFilteredData.restaurantName}
                    </p>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">
                      from{' '}
                      {getFullDateInCustomTimezone(
                        tipsAssignData.fromDate,
                        currentFilteredData.timezone,
                      )}{' '}
                      to{' '}
                      {getFullDateInCustomTimezone(
                        tipsAssignData.toDate,
                        currentFilteredData.timezone,
                      )}
                    </p>
                  </div>
                </div>
                <div className="border-t border-gray-200">
                  <dl>
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Undistributed tips</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {(tipsAssignData.totalUndistributedTips || 0).toFixed(2)}{' '}
                        {currentFilteredData.currency}
                      </dd>
                    </div>
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Assigned tips</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {(totalAssignedTips || 0).toFixed(2)} {currentFilteredData.currency}
                      </dd>
                    </div>
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 items-center">
                      <dt className="text-sm font-medium text-gray-500">Complete distribution</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        <FormButton
                          text="Assign"
                          noMargin
                          disabled={
                            (totalAssignedTips || 0) !==
                              (tipsAssignData.totalUndistributedTips || 0) ||
                            (tipsAssignData.totalUndistributedTips || 0) === 0 ||
                            isCreatingAssignment
                          }
                          isLoading={isCreatingAssignment}
                          onClick={() => {
                            onDistributeTips();
                          }}
                        />
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>

            <List source={tipsAssignData.employees}>
              <DataColumn
                name="Name"
                dbColumnName="first_name"
                render={(x) => {
                  return `${x.first_name || ''} ${x.last_name || ''}`.trim();
                }}
                headerClass="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                dataCellClass="px-6 py-4 text-left whitespace-nowrap text-sm text-gray-900"
              />
              <DataColumn
                name="Assigned Tips"
                dbColumnName="assigned_tips"
                render={(x) => {
                  const currentVal = assignments[x._id];

                  return renderRangeSlider(x._id, currentVal.value);
                }}
                headerClass="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                dataCellClass="px-6 py-4 text-left whitespace-nowrap text-sm text-gray-900"
              />
            </List>
          </>
        )}
      </PageContent>
    </ListLayout>
  );
};
